/**
 * Cart drawer upsell - single-flight fetch + HTML cache so ATC / section re-renders
 * don't abort and re-request (which made the upsell blink twice).
 * Always prune products already in the cart after applying HTML.
 */
(function () {
  const cache = new Map();
  const inflight = new Map();

  function productIdFromUrl(url) {
    if (!url) return '';
    try {
      const u = new URL(url, window.location.origin);
      return u.searchParams.get('product_id') || '';
    } catch (e) {
      const m = String(url).match(/product_id=([^&]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    }
  }

  function extractInner(text) {
    const wrap = document.createElement('div');
    wrap.innerHTML = text;
    const node = wrap.querySelector('cart-recommendations');
    return node && node.innerHTML.trim() ? node.innerHTML : '';
  }

  function pruneHost(el) {
    const host = el && (el.closest('#mini-cart') || document.getElementById('mini-cart'));
    if (typeof window.BellaCartPruneUpsell === 'function') {
      window.BellaCartPruneUpsell(host);
      return;
    }
    // Fallback if bella-cart-drawer.js has not booted yet.
    if (!host || !el) return;
    const ids = new Set();
    const handles = new Set();
    host.querySelectorAll('cart-items [data-product-id]').forEach((node) => {
      const id = node.getAttribute('data-product-id');
      if (id) ids.add(String(id));
    });
    host.querySelectorAll('cart-items a[href*="/products/"]').forEach((a) => {
      const m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      if (m && m[1]) handles.add(decodeURIComponent(m[1]));
    });
    el.querySelectorAll('.bella-cart__recs-item').forEach((li) => {
      const id = li.getAttribute('data-product-id');
      if (id && ids.has(String(id))) {
        li.remove();
        return;
      }
      const link = li.querySelector('a[href*="/products/"]');
      const hm = link && (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      if (hm && hm[1] && handles.has(decodeURIComponent(hm[1]))) li.remove();
    });
  }

  function fetchRecsHtml(url) {
    if (!url) return Promise.resolve('');
    if (cache.has(url)) return Promise.resolve(cache.get(url));
    if (inflight.has(url)) return inflight.get(url);

    const req = fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const inner = extractInner(text);
        if (inner) cache.set(url, inner);
        inflight.delete(url);
        return inner;
      })
      .catch((e) => {
        inflight.delete(url);
        throw e;
      });

    inflight.set(url, req);
    return req;
  }

  function stripAnimate(el) {
    if (!el) return;
    el.removeAttribute('data-animate');
    Array.from(el.attributes).forEach((attr) => {
      if (attr.name.indexOf('data-animate-delay') === 0) el.removeAttribute(attr.name);
    });
  }

  function applyHtml(el, html) {
    if (!el || !html) return false;
    el.innerHTML = html;
    el.dataset.loaded = 'true';
    delete el.dataset.loading;
    pruneHost(el);
    if (el.dataset.url) cache.set(el.dataset.url, el.innerHTML);
    return true;
  }

  window.BellaCartRecs = {
    cache,
    getCached(url) {
      return (url && cache.get(url)) || '';
    },
    setCached(url, html) {
      if (url && html) cache.set(url, html);
    },
    clearCache() {
      cache.clear();
      inflight.clear();
    },
    fetch: fetchRecsHtml,
    productIdFromUrl,
    stripAnimate,
    applyHtml,
    pruneHost
  };

  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this._observer = null;
      this._fetchToken = 0;
    }

    connectedCallback() {
      if (this.dataset.loaded === 'true') {
        pruneHost(this);
        return;
      }
      if (this.querySelector('.bella-cart__recs-item')) {
        this.dataset.loaded = 'true';
        pruneHost(this);
        const url = this.dataset.url;
        if (url && this.innerHTML.trim()) cache.set(url, this.innerHTML);
        return;
      }
      if (!this.dataset.url) return;

      const cached = cache.get(this.dataset.url);
      if (cached) {
        applyHtml(this, cached);
        return;
      }

      // Fetch immediately - don't wait for IntersectionObserver (drawer slide-in
      // + delayed fetch = empty fade then second paint).
      this.loadRecommendations();
    }

    disconnectedCallback() {
      if (this._observer) {
        try {
          this._observer.disconnect();
        } catch (e) {}
        this._observer = null;
      }
      // Do not abort shared inflight fetch - next mount reuses the same promise/cache.
      this._fetchToken += 1;
    }

    loadRecommendations() {
      const url = this.dataset.url;
      if (!url || this.dataset.loaded === 'true') return;
      if (this.dataset.loading === 'true' && inflight.has(url)) return;

      this.dataset.loading = 'true';
      const token = ++this._fetchToken;

      fetchRecsHtml(url)
        .then((inner) => {
          if (token !== this._fetchToken || !this.isConnected) return;
          if (this.dataset.loaded === 'true') {
            pruneHost(this);
            return;
          }
          if (this.querySelector('.bella-cart__recs-item')) {
            this.dataset.loaded = 'true';
            delete this.dataset.loading;
            pruneHost(this);
            return;
          }
          if (inner) applyHtml(this, inner);
          else {
            this.dataset.loaded = 'true';
            delete this.dataset.loading;
          }
        })
        .catch((e) => {
          if (e && e.name === 'AbortError') return;
          console.error(e);
          if (token === this._fetchToken) delete this.dataset.loading;
        });
    }
  }

  if (!customElements.get('cart-recommendations')) {
    customElements.define('cart-recommendations', CartRecommendations);
  }
})();
